<template>
  <div>
    <h2>Energiebilanz</h2>
    <p>
      Visualisiert den gesamten Energiefluss des Projekts — von der eingesetzten
      <b>Endenergie</b> über die <b>Maßnahmen</b> bis zur erzeugten
      <b>Nutzenergie</b>. Referenz- und Planszenario werden gegenübergestellt,
      sodass Einsparungen direkt sichtbar werden.
    </p>

    <h3>Kurz zum Start</h3>
    <ol>
      <li>Oben die Verbrauchstabelle lesen: wie viel Energie geht wohin.</li>
      <li>Darunter die Sankey-Diagramme — links Referenz, rechts Geplant.</li>
      <li>Schmalere Bänder im Planszenario sind die Einsparung.</li>
    </ol>

    <h3>Abkürzungen</h3>
    <dl>
      <dt><strong>Endenergie</strong></dt>
      <dd>Was eingekauft und abgerechnet wird — Strom, Gas, Fernwärme an der Gebäudegrenze.</dd>

      <dt><strong>Nutzenergie</strong></dt>
      <dd>Was davon beim Zweck ankommt: Wärme, Kälte, Licht, mechanische Arbeit. Die
        Differenz zur Endenergie sind die Verluste der Anlagentechnik.</dd>

      <dt><strong>Referenz / Geplant</strong></dt>
      <dd>Zustand vor und nach der Maßnahme. Beide werden gerechnet, nicht gemessen.</dd>

      <dt><strong>Baseline</strong></dt>
      <dd>Tatsächlich gemessener Verbrauch aus den Projektdetails. Erscheint nur, wenn
        Messdaten hinterlegt sind, und dient als Wirklichkeitsprobe für die Rechnung.</dd>

      <dt><strong>CO₂e</strong></dt>
      <dd>CO₂-Äquivalent: andere Treibhausgase werden in die Wirkung von CO₂ umgerechnet
        und mitgezählt.</dd>

      <dt><strong>Sankey</strong></dt>
      <dd>Flussdiagramm, in dem die Breite eines Bandes der Energiemenge entspricht.</dd>
    </dl>

    <h3>Verbrauchstabellen</h3>
    <ul>
      <li><b>Energieverbrauch:</b> Endenergie nach Energieträger — optional mit Baseline, Referenz, Geplant und Einsparung in kWh / %.</li>
      <li><b>Verbräuche je Maßnahme:</b> Aufgeschlüsselt nach Gruppen und Einzelmaßnahmen mit Referenz, Geplant und Einsparung.</li>
    </ul>

    <h3>Sankey-Diagramme</h3>
    <dl>
      <dt><strong>Energiebilanz Referenz / Geplant (mit Maßnahmen)</strong></dt>
      <dd>Endenergie → Maßnahme → Nutzenergie. Die Bandbreite zeigt die Energiemenge —
        schmalere Bänder im Planszenario = Einsparung. Auf der rechten Seite steht neben
        der Nutzenergie auch der Teil, der als <b>Verluste</b> anfällt (graues Band);
        beide Seiten eines Maßnahmenknotens sind deshalb gleich breit. Genau an diesen
        Verlusten setzen Effizienzmaßnahmen an — im Planszenario werden sie schmaler.</dd>

      <dt><strong>Energiebilanz Summe Referenz / Geplant</strong></dt>
      <dd>Aggregierte Zuordnung Endenergie → Nutzenergie ohne Maßnahmenebene.</dd>

      <dt><strong>CO₂e-Bilanz Referenz / Geplant (Emissionen Maßnahme CO₂e)</strong></dt>
      <dd>Emissionsfluss in kg/a: links der <b>Energieträger</b>, in der Mitte die
        Maßnahme, rechts die Gesamtmenge. So ist je Maßnahme zu sehen, <i>woraus</i>
        ihre Emissionen stammen — bei einer Maßnahme am Gaskessel steht dort Erdgas,
        bei Beleuchtung oder Antrieben Strom. Gutschriften für selbst verbrauchten
        erneuerbaren Strom mindern den Strang, den sie verdrängen.</dd>

      <dt><strong>CO₂e-Bilanz Summe Referenz / Geplant</strong></dt>
      <dd>Die Emissionen des Standorts, <b>aufgeteilt nach Endenergieträger</b>:
        ein Strang je Träger, zusammenlaufend auf die Gesamtmenge. Weil 1 kWh
        Erdgas und 1 kWh Netzstrom sehr verschieden wiegen, ist hier zu sehen,
        welcher Träger die Emissionen tatsächlich treibt — und damit, an welcher
        Stelle eine Maßnahme überhaupt etwas bewirkt. Ist für einen Träger kein
        CO₂-Faktor hinterlegt, bleibt nur ein einzelner Strang mit der
        Gesamtmenge.</dd>

      <dt><strong>Endenergie-Mix (Donut)</strong></dt>
      <dd>Prozentuale Zusammensetzung der Energieträger — Referenz vs. Geplant nebeneinander.</dd>
    </dl>

    <h3>Deckungsgrad und Fortschreibung</h3>
    <dl>
      <dt><strong>Deckungsgrad: Baseline gegen Referenz</strong></dt>
      <dd>Die <b>Baseline</b> ist gemessen, <b>Referenz</b> und <b>Geplant</b> sind
        gerechnet. Der Deckungsgrad sagt, welchen Anteil des gemessenen Verbrauchs die
        erfassten Maßnahmen abbilden. <b>Unter 100 %</b> ist der Normalfall — die
        Maßnahmen decken einen Teil des Standorts. <b>Über 100 %</b> ist ein Warnzeichen:
        das Modell verbraucht mehr als die Zähler, die Einsparungen sind dann zu hoch
        angesetzt. Prüfen Sie in dem Fall Betriebsstunden und Leistungsangaben.</dd>

      <dt><strong>Fortschreibung der Regression</strong></dt>
      <dd>Standardmäßig aus, und das mit Absicht: die einzige unabhängige Variable
        dieses Modells ist das <i>Jahr</i>. Eine Fortschreibung besagt daher nur „so wie
        bisher weiter" — sie ist keine Prognose im Sinne von DIN EN 17669 Anhang A, wo
        gegen <b>relevante Variablen</b> (Gradtage, Produktionsmenge) normiert wird.
        Eingeschaltet zeigt sie das gestrichelte Band der nächsten Jahre samt
        <b>Prognoseintervall</b>; mit wenigen Messjahren wird dieses Band sehr breit,
        und genau das ist die Aussage. „Maßnahmen wirksam ab" legt fest, ab welchem Jahr
        die Einsparungen von der Fortschreibung abgezogen werden.</dd>
    </dl>

    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Datenexport</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Jedes Diagramm bietet über das Drei-Punkte-Menü (⋮) zwei Optionen:
          </p>
          <ul>
            <li><b>Grafik herunterladen:</b> PNG-Export der aktuellen Darstellung.</li>
            <li><b>Daten exportieren:</b> Sankey-/Chart-Daten als Excel-Datei.</li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Hinweise</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ul>
            <li>
              Auf der Seite
              <router-link
                v-if="projectId"
                :to="{ name: 'ProjectDashboard', params: { projectId: String(projectId) } }"
              >Maßnahmen</router-link><span v-else>Maßnahmen</span>
              ausgeschlossene Maßnahmen fließen nicht in die Berechnung ein.
            </li>
            <li>„Gruppendetails anzeigen" wechselt zwischen Gruppen- und Einzelmaßnahmen-Ansicht in den Sankey-Diagrammen.</li>
            <li>Die Baseline-Zeile erscheint nur, wenn Messdaten hinterlegt sind.</li>
            <li>CO₂e-Diagramme werden nur angezeigt, wenn Emissionsdaten vorhanden sind.</li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-footer class="mt-10">
      <small>
        Das Fenster kann an der linken Kante angepasst und über „Hilfe" wieder
        geschlossen werden.
      </small>
    </v-footer>
  </div>
</template>

<script setup lang="ts">
/*
 * `projectId` aus dem Store, damit der Verweis auf die Maßnahmenseite
 * aufgelöst werden kann: die Route `ProjectDashboard` verlangt den Parameter
 * `projectId`. Ohne ihn blieb der `router-link` wirkungslos — derselbe Aufbau
 * wie in `ProjectCalcHelp.vue`.
 */
import { computed } from 'vue';
import { useProjectStore } from '@/stores/projectStore';

const store = useProjectStore();
const projectId = computed(() => store.currentProject?.id ?? null);
</script>

<style scoped></style>
